import { PdfBrandConfig } from '@common/types';
import { useMemo } from 'react';
import { AgencyBrand, BrandingMode } from 'enums';
import { useSearchAgency } from 'hooks/api/agency';
import { getEnvironment } from 'utils';

const getDefaultFlowConfig = (websiteBaseUrl: string): PdfBrandConfig => ({
  agencyBrand: AgencyBrand.Flow,
  coverLetterTextColor: '#FFFFFF',
  companyName: 'Flow Specialty Insurance Services LLC',
  coverFooterText: 'Flow Specialty Insurance Services, LLC  |  CA License Number: 6008097',
  style: {
    pageMargins: [30, 90, 30, 50],
    logoStyle: {
      margin: [0, 200, 0, 0],
    },
    footerStyle: {
      margin: [0, 30, 0, 0],
    },
  },
  showPageNumbers: false,
  pdfCoverBgImage: `${websiteBaseUrl}/client-branding/flow/pdf-cover-bg.png`,
  pdfHeaderBgImage: `${websiteBaseUrl}/client-branding/flow/pdf-header-bg.png`,
  pdfFooterBgImage: `${websiteBaseUrl}/client-branding/flow/pdf-footer-bg.png`,
  topLeftTextDecor: `${websiteBaseUrl}/client-branding/flow/top-left-text-decor.png`,
  bottomRightTextDecor: `${websiteBaseUrl}/client-branding/flow/bottom-right-text-decor.png`,
  logoImage: `${websiteBaseUrl}/client-branding/flow/logo.png`,
});

export type PdfBrandConfigs = {
  [BrandingMode.Flow]: PdfBrandConfig;
  [BrandingMode.Organization]: PdfBrandConfig;
};

interface UsePdfBrandConfigsProps {
  organizationId: string;
}

export function usePdfBrandConfigs({ organizationId }: UsePdfBrandConfigsProps) {
  const environment = getEnvironment();
  const { items: agencies, isLoading } = useSearchAgency({
    filter: { organizationId },
    enabled: !!organizationId,
  });
  const baseUrl = environment === 'prod' ? 'https://public.flowspecialty.com' : 'https://public.dev.flowspecialty.com';

  const agency = agencies?.[0];

  const brandConfigs = useMemo((): PdfBrandConfigs | undefined => {
    if (isLoading) {
      return undefined;
    }

    return {
      [BrandingMode.Flow]: getDefaultFlowConfig(baseUrl),
      [BrandingMode.Organization]: agency?.brandConfig || getDefaultFlowConfig(baseUrl),
    };
  }, [agency, isLoading, baseUrl]);

  const isClientBranded = useMemo(
    () =>
      !!brandConfigs?.[BrandingMode.Organization] &&
      brandConfigs[BrandingMode.Organization].agencyBrand !== AgencyBrand.Flow,
    [brandConfigs],
  );

  return {
    brandConfigs,
    isLoading,
    isClientBranded,
  };
}
